import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { changePrivateSession } from "@/shared/services/privateSessionChange";
import { getPrivateSessionState } from "@/lib/api/private-session";
import { App } from "@capacitor/app";
import { isSellerNativeApp } from "@/lib/native/runtime";
import {
  createSellerImpersonationHandoffHandler,
  parseSellerImpersonationHandoffUrl,
  redeemSellerImpersonationHandoff,
} from "../../lib/native/impersonation-handoff";

const HANDOFF_LAUNCH_SEEN_KEY = "trioty-seller-impersonation-launch-seen";

function hasSeenLaunchHandoff(): boolean {
  try {
    return sessionStorage.getItem(HANDOFF_LAUNCH_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markLaunchHandoffSeen(): void {
  try {
    // This is only a non-secret marker. The handoff code itself is never
    // persisted; the marker prevents a native WebView reload from asking
    // Capacitor for the same already-consumed launch URL again.
    sessionStorage.setItem(HANDOFF_LAUNCH_SEEN_KEY, "1");
  } catch {
    // Storage can be unavailable in a restricted WebView; history navigation
    // below still prevents the normal handoff path from doing a hard reload.
  }
}

function clearLaunchHandoffMarker(): void {
  try {
    sessionStorage.removeItem(HANDOFF_LAUNCH_SEEN_KEY);
  } catch {
    // Best effort only.
  }
}

export function NativeImpersonationBootstrap() {
  const client = useQueryClient();
  useEffect(() => {
    if (!isSellerNativeApp()) {
      return;
    }

    let active = true;
    let navigationGeneration: number | undefined;
    const handleImpersonationUrl = createSellerImpersonationHandoffHandler({
      redeem: async (handoff) => {
        const expectedGeneration = getPrivateSessionState().generation + 1;
        navigationGeneration = expectedGeneration;
        try {
          const result = await changePrivateSession(client, () => redeemSellerImpersonationHandoff(handoff));
          const session = getPrivateSessionState();
          if (!active || session.changing || session.generation !== expectedGeneration) throw new DOMException("Session change superseded", "AbortError");
          return result;
        }
        catch (failure) {
          const session = getPrivateSessionState();
          if (!active || session.changing || session.generation !== expectedGeneration) throw new DOMException("Session change superseded", "AbortError");
          throw failure;
        }
      },
      // Do not reload the WebView here. Capacitor's getLaunchUrl() continues to
      // expose the original intent/custom-scheme URL for the lifetime of the
      // native activity. A hard reload would therefore redeem the same one-time
      // handoff again and send the app into a refresh/login loop. React Router
      // listens for popstate and can transition to the authenticated store route
      // without re-running the native bootstrap.
      navigate: (path) => {
        const session = getPrivateSessionState();
        if (!active || session.changing || session.generation !== navigationGeneration) return;
        window.history.replaceState(window.history.state, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
    });
    const receiveEvent = (url: string) => {
      if (!parseSellerImpersonationHandoffUrl(url)) return;
      // A new appUrlOpen event is a fresh admin action. Allow it even when a
      // previous launch URL was already handled in this WebView session.
      clearLaunchHandoffMarker();
      if (active) void handleImpersonationUrl(url);
    };

    const listenerHandle = App.addListener("appUrlOpen", ({ url }) => {
      receiveEvent(url);
    });

    void App.getLaunchUrl().then((launch) => {
      const url = launch?.url;
      if (!url || !parseSellerImpersonationHandoffUrl(url)) return;
      if (hasSeenLaunchHandoff()) return;
      markLaunchHandoffSeen();
      if (active) void handleImpersonationUrl(url);
    });

    return () => {
      active = false;
      void listenerHandle.then((handle) => handle.remove());
    };
  }, [client]);

  return null;
}
